import { matchPath } from 'react-router-dom';
import { RevealStrategy } from 'Design-system/app/static/scripts/support-widget';
import { SupportState } from 'Douleutaras/redux/data/settings/supportSlice';
import { SupportSurface, SupportTrigger } from 'Douleutaras/app/lib/posthog/posthog.types';
import { URLS } from 'Douleutaras/constants/urls';
import { LANGUAGE_URL_PREFIXES } from 'Douleutaras/constants/localization';

/**
 * Where the floating support widget lives in this app, and how it behaves there.
 *
 * Data, not conditionals: a surface whose rules change edits its row, and a new
 * surface adds one. Exit intent (#5622) is deliberately not a row — it is an
 * embedded entry point with no launcher and is not governed by the widget flag.
 */
export interface SupportSurfacePolicy {
  surface: SupportSurface;
  trigger: SupportTrigger;
  reveal: RevealStrategy;
  /** Visible time before a `timer` reveal. */
  revealDelayMs: number;
  /** Mount as the resting circle, skipping the reveal peek. */
  startCollapsed: boolean;
  /**
   * Render the closed (contact-form) widget even when the admin chose to hide it
   * outside working hours. The master widget flag still wins.
   */
  ignoreShowOutsideWorkingHours: boolean;
  /** Identify the logged-in customer before capturing. */
  identified: boolean;
}

export interface ResolvedSupportSurface {
  policy: SupportSurfacePolicy;
  jobId?: string;
}

/** What the widget renders as, or `undefined` for nothing at all. */
export type FloatingSupportState = 'available' | 'offline';

// A fixed beat so the reveal catches the eye — not the admin `timer_seconds`, which #5620 excludes.
export const JOB_POSTED_FLOW_REVEAL_DELAY_MS = 2000;

// #5620: always visible, identified, independent of the hour flag; peeks on reveal like BSDIR.
const JOB_POSTED_FLOW: Omit<SupportSurfacePolicy, 'surface'> = {
  trigger: SupportTrigger.ALWAYS_VISIBLE,
  reveal: 'timer',
  revealDelayMs: JOB_POSTED_FLOW_REVEAL_DELAY_MS,
  startCollapsed: false,
  ignoreShowOutsideWorkingHours: true,
  identified: true
};

const CLIENT_DASHBOARD = URLS.CLIENT_DASHBOARD.INDEX;

// Distinct review views that happen to live under the job route.
const JOB_SUB_VIEWS_WITHOUT_WIDGET = [
  URLS.CLIENT_DASHBOARD.JOB.REVIEW_BID.split('/')[0],
  URLS.CLIENT_DASHBOARD.JOB.REVIEW_NPS
];

interface SurfaceRoute {
  patterns: Array<string>;
  end: boolean;
  policy: SupportSurfacePolicy;
  /** Where the job lives on this route: the path param or the `jobId` query param. */
  jobIdFrom: 'path' | 'query' | 'none';
}

const withLanguagePrefixes = (path: string): Array<string> =>
  [path, ...LANGUAGE_URL_PREFIXES.map(prefix => `/${prefix}${path}`)];

const SURFACE_ROUTES: Array<SurfaceRoute> = [
  {
    patterns: [CLIENT_DASHBOARD],
    end: true,
    jobIdFrom: 'none',
    policy: { ...JOB_POSTED_FLOW, surface: SupportSurface.JOB_LIST }
  },
  {
    patterns: [`${CLIENT_DASHBOARD}/${URLS.CLIENT_DASHBOARD.JOB.INDEX}/*`],
    end: false,
    jobIdFrom: 'path',
    policy: { ...JOB_POSTED_FLOW, surface: SupportSurface.JOB_PAGE }
  },
  {
    patterns: [`${CLIENT_DASHBOARD}/${URLS.CLIENT_DASHBOARD.PRO_LIST}/*`],
    end: false,
    jobIdFrom: 'query',
    policy: { ...JOB_POSTED_FLOW, surface: SupportSurface.PROS_LIST }
  },
  {
    patterns: withLanguagePrefixes(`${URLS.PRO_PROFILE.INDEX}/*`),
    end: false,
    jobIdFrom: 'query',
    policy: { ...JOB_POSTED_FLOW, surface: SupportSurface.PROS_PROFILE }
  }
];

const jobIdFromQuery = (search: string): string | undefined => {
  const value = new URLSearchParams(search).get('jobId');

  return value || undefined;
};

const resolveJobId = (route: SurfaceRoute, params: { jobId?: string; '*'?: string }, search: string) => {
  if (route.jobIdFrom === 'path') {
    return params.jobId;
  }

  return route.jobIdFrom === 'query' ? jobIdFromQuery(search) : undefined;
};

/** The surface a location belongs to, or `undefined` where the widget has no requirement. */
export const resolveSupportSurface = (pathname: string, search = ''): ResolvedSupportSurface | undefined => {
  for (const route of SURFACE_ROUTES) {
    for (const pattern of route.patterns) {
      const match = matchPath({ path: pattern, end: route.end }, pathname);

      if (!match) {
        continue;
      }

      const subView = (match.params['*'] || '').split('/')[0];

      if (route.jobIdFrom === 'path' && JOB_SUB_VIEWS_WITHOUT_WIDGET.includes(subView)) {
        return undefined;
      }

      return { policy: route.policy, jobId: resolveJobId(route, match.params, search) };
    }
  }

  return undefined;
};

/**
 * How the widget renders on a surface, from the resolved `state` plus the one
 * per-surface override #5620 asks for.
 *
 * Flag hierarchy, as `get_support_state` resolves it:
 * 1. `CUSTOMER_SUPPORT_WIDGET` off → nothing, on every surface (`widget.enabled`).
 * 2. Salesforce kill switch off, or 3. outside working hours → closed.
 * 4. Otherwise → available.
 *
 * Closed renders `offline` or `hidden` per `SHOW_SUPPORT_WIDGET_OUTSIDE_WORKING_HOURS`.
 * #5620 states that flag does not apply to the job-posted flow, so with the widget on,
 * any closed answer renders the contact-form widget here. This is the only place that
 * reads past `state`, and it reads the master switch only — never the hours.
 */
export const resolveFloatingSupportState = (
  support: SupportState,
  policy: SupportSurfacePolicy
): FloatingSupportState | undefined => {
  if (!support.widget.enabled) {
    return undefined;
  }

  if (support.state === 'available') {
    return 'available';
  }

  if (support.state === 'offline' || policy.ignoreShowOutsideWorkingHours) {
    return 'offline';
  }

  return undefined;
};
